import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Table from '../table/Table.js';
import { createTextColumn } from '../table/Column.js';

export const MASTER_CLINIC_ID = 'MASTER';

export const addClinicColumns = [
  createTextColumn('clinicName', 'Name', (c) => c.clinicName, { sortable: false, filterable: false }),
];

export const removeClinicColumns = [
  createTextColumn('clinicName', 'Name', (c) => c.clinicName, { sortable: false, filterable: false }),
];

export const BulkAddToClinicModal = ({ isOpen, onClose, onConfirm, users, clinics, loading }) => {
  const [selectedClinicIds, setSelectedClinicIds] = useState(new Set());

  useEffect(() => {
    if (!isOpen) {
      setSelectedClinicIds(new Set());
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (selectedClinicIds.size > 0) {
      onConfirm([...selectedClinicIds]);
    }
  };

  const filteredClinics = clinics.filter(c => c.clinicId !== MASTER_CLINIC_ID);

  if (!isOpen || !users || users.length === 0) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>Add Users to Clinics</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="modal-content">
          <p>Select clinics to add {users.length} user{users.length > 1 ? 's' : ''} to:</p>

          <Table
            id="BulkAddClinicTable"
            data={filteredClinics}
            columns={addClinicColumns}
            getRowKey={(c) => c.clinicId}
            enableSelection={true}
            selectedRows={selectedClinicIds}
            onSelectionChange={setSelectedClinicIds}
            emptyMessage="No clinics available."
          />
        </div>
        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="admin-submit-button"
            onClick={handleConfirm}
            disabled={loading || selectedClinicIds.size === 0}
          >
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              'Add to Clinics'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export const BulkRemoveFromClinicModal = ({ isOpen, onClose, onConfirm, users, clinics, loading }) => {
  const [selectedClinicIds, setSelectedClinicIds] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedClinicIds(new Set());
      setShowDeleteConfirm(false);
    }
  }, [isOpen]);

  const handleSelectionChange = (newSelection) => {
    setSelectedClinicIds(newSelection);
    setShowDeleteConfirm(false);
  };

  const filteredClinics = clinics.filter(c => c.clinicId !== MASTER_CLINIC_ID);

  const usersToBeDeleted = useMemo(() => {
    if (selectedClinicIds.size === 0) return [];
    return users.filter(user => {
      const remaining = (user.clinicIds || []).filter(id => !selectedClinicIds.has(id));
      return remaining.length === 0;
    });
  }, [users, selectedClinicIds]);

  const usersWithNoEffect = useMemo(() => {
    if (selectedClinicIds.size === 0) return [];
    return users.filter(user => {
      const intersection = (user.clinicIds || []).filter(id => selectedClinicIds.has(id));
      return intersection.length === 0;
    });
  }, [users, selectedClinicIds]);

  const handleConfirm = () => {
    if (usersToBeDeleted.length > 0 && !showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }
    if (selectedClinicIds.size > 0) {
      onConfirm([...selectedClinicIds]);
    }
  };

  if (!isOpen || !users || users.length === 0) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>Remove Users from Clinics</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="modal-content">
          <p>Select clinics to remove {users.length} user{users.length > 1 ? 's' : ''} from:</p>

          <Table
            id="BulkRemoveClinicTable"
            data={filteredClinics}
            columns={removeClinicColumns}
            getRowKey={(c) => c.clinicId}
            enableSelection={true}
            selectedRows={selectedClinicIds}
            onSelectionChange={handleSelectionChange}
            emptyMessage="No clinics available."
          />

          {usersToBeDeleted.length > 0 && (
            <div style={{
              background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px',
              padding: '10px 12px', marginTop: '10px', color: '#856404', fontSize: '13px'
            }}>
              <strong>Warning:</strong> The following user{usersToBeDeleted.length > 1 ? 's' : ''} will be
              removed from all clinics and <strong>deleted</strong>:
              <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
                {usersToBeDeleted.map(u => (
                  <li key={u.userId}>{u.name} ({u.email})</li>
                ))}
              </ul>
            </div>
          )}

          {usersWithNoEffect.length > 0 && selectedClinicIds.size > 0 && (
            <div style={{
              background: '#e8f4fd', border: '1px solid #b8daff', borderRadius: '6px',
              padding: '10px 12px', marginTop: '10px', color: '#004085', fontSize: '13px'
            }}>
              {usersWithNoEffect.length} user{usersWithNoEffect.length > 1 ? 's are' : ' is'} not
              in any of the selected clinics and will be unaffected.
            </div>
          )}

          {showDeleteConfirm && (
            <div style={{
              background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '6px',
              padding: '10px 12px', marginTop: '10px', color: '#721c24', fontSize: '13px',
              fontWeight: 'bold'
            }}>
              Are you sure? {usersToBeDeleted.length} user{usersToBeDeleted.length > 1 ? 's' : ''} will
              be permanently deleted. Click the button again to confirm.
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="admin-submit-button"
            onClick={handleConfirm}
            disabled={loading || selectedClinicIds.size === 0}
            style={showDeleteConfirm ? { background: '#dc3545' } : {}}
          >
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : showDeleteConfirm ? (
              'Confirm Removal & Deletion'
            ) : (
              'Remove from Clinics'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
